import './Datepicker.css';

import React, { useState, useRef, useEffect } from 'react';
import classnames from 'classnames';
import { DateRange, Matcher } from 'react-day-picker';
import GlobalProps from '../../globalProps';
import { useForm } from '../Form';
import { Icon } from '../../Icon/Icon';
import { formatDate, formatDateTime, TimeFrame, formatRangeOfDays, toFullDaysRange } from '../../../util/util';
import { TextField } from '../TextField/TextField';
import { Hourpicker } from './Hourpicker';
import { t } from '../../../locales/translate';
import { Tooltip } from '../../Tooltip/Tooltip';
import * as PopperJS from 'popper.js';
import { StyledDayPicker } from '../styled_day_picker/styled_day_picker';

export type DatepickerValue = TimeFrame | Date | undefined | null;

interface Props extends GlobalProps {
  name: string;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  description?: string;
  errorMessage?: string;
  position?: PopperJS.Placement;
  rangeOfDays?: boolean;
  hourpicker?: boolean;
  disabledDays?: Matcher | Matcher[];
  icon?: boolean;
  onChange?: (date: DatepickerValue) => void;
  placeholder?: boolean;
  displayOptional?: boolean;
}

export function Datepicker({
  name,
  label,
  required,
  disabled,
  readonly,
  description,
  errorMessage,
  className,
  rangeOfDays,
  style,
  position = 'bottom-start',
  hourpicker,
  disabledDays,
  onChange,
  icon = true,
  placeholder,
  displayOptional,
}: Props): JSX.Element {
  const [rawValue, setValue, triedToSubmit, reportValidationStatus] = useForm<DatepickerValue>(name);
  const [active, setActive] = useState(false);

  const inputRef: React.MutableRefObject<HTMLInputElement | null> = useRef(null);
  const containerRef: React.MutableRefObject<HTMLDivElement | null> = useRef(null);

  useEffect(() => {
    return () => {
      reportValidationStatus(true);
    };
  }, []);

  const date = rangeOfDays ? undefined : (rawValue as Date);
  const [from, to] = rawValue && rangeOfDays ? (rawValue as TimeFrame) : [undefined, undefined];

  if (required) {
    reportValidationStatus(rangeOfDays ? !!from && !!to : !!date);
  }

  const validationMessage =
    errorMessage || (triedToSubmit && inputRef.current && inputRef.current.validationMessage) || undefined;

  function handleRangeDayClick(dateRange: DateRange): void {
    const adjustedRange = toFullDaysRange([dateRange.from, dateRange.to]);
    if (onChange) {
      onChange(adjustedRange);
    }
    setValue(adjustedRange);
  }

  function handleOneDayClick(day: Date): void {
    if (onChange) {
      onChange(day);
    }
    setValue(day);
    if (!hourpicker) {
      setActive(false);
    }
  }

  function handleTimeUpdate(updateDate: Date, date?: 'from' | 'to'): void {
    if (date === 'from') {
      return setValue([updateDate, rawValue?.[1]]);
    } else if (date === 'to') {
      return setValue([rawValue?.[0], updateDate]);
    } else {
      setValue(updateDate);
    }

    onChange?.(updateDate);
  }

  return (
    <div
      className={classnames('datepicker', { 'datepicker--range-of-days': rangeOfDays }, className)}
      style={style}
      ref={containerRef}
    >
      <Tooltip
        position={position}
        mode="click"
        gap="none"
        arrow={false}
        rounded={false}
        open={active}
        onChange={(open: boolean) => {
          if (!open) {
            setActive(false);
          }
        }}
        dontCloseOnParentClick
        reference={
          <div onFocus={() => setActive(!readonly)} id={name} className="datepicker__input" ref={inputRef}>
            <TextField
              displayOptional={displayOptional}
              name={name}
              placeholder={
                rawValue
                  ? rangeOfDays
                    ? formatRangeOfDays([from, to])
                    : formatDateTime(date)
                  : placeholder
                    ? t.actions.select(t.model.datetime._name)
                    : ''
              }
              disabled={disabled}
              readOnly
              required={required}
              icon={icon ? 'calendar--sm' : undefined}
              label={label}
              // Casted to any because e depends on rangeOfDays and hourpicker variables
              // eslint-disable-next-line
              formatter={(e: any) => {
                return rangeOfDays ? formatRangeOfDays(e) : hourpicker ? formatDateTime(e) : formatDate(e);
              }}
              actions={
                rawValue && (
                  <span onClick={() => setValue(null)}>
                    <Icon name="remove-circle--sm" />
                  </span>
                )
              }
            />
          </div>
        }
      >
        <StyledDayPicker
          {...(rangeOfDays
            ? {
                mode: 'range',
                selected: { from, to },
                onSelect: handleRangeDayClick,
                modifiers: { start: from, end: to },
              }
            : { mode: 'single', selected: date, onSelect: handleOneDayClick, modifiers: { oneDay: date } })}
          required
          disabled={disabledDays}
          numberOfMonths={1}
        />
        {hourpicker && (
          <div className="datepicker__hourpicker">
            {rangeOfDays ? (
              <>
                <Hourpicker
                  label={t.component.picker.startTime}
                  date={from}
                  onChange={(value) => handleTimeUpdate(value, 'from')}
                />
                <Hourpicker
                  label={t.component.picker.endTime}
                  date={to}
                  onChange={(value) => handleTimeUpdate(value, 'to')}
                />
              </>
            ) : (
              <Hourpicker date={date} onChange={handleTimeUpdate} />
            )}
          </div>
        )}
      </Tooltip>
      <div className="datepicker__description">
        {description && <div className="input__description">{description}</div>}
        {validationMessage && <div className="input__error-message">{validationMessage}</div>}
      </div>
    </div>
  );
}
